import React from 'react';
import { useTranslation } from 'react-i18next';
import { isVideo } from '../../models/Media';
import { IPreviewProps } from 'react-dropzone-uploader';
import ReactPlayer from 'react-player';
import iziToast from 'izitoast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo, faCheck, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';

const DropzoneItem = (props: IPreviewProps) => {

    const [t] = useTranslation();

    const { name, percent, status, type, previewUrl } = props.meta;
    const { file, cancel, remove, xhr } = props.fileWithMeta;

    const isUploading = status === "uploading";
    const isDone = status === "done";
    const isError =
        status.startsWith("rejected_") ||
        status.startsWith("error_") ||
        status.startsWith("exception_");

    // Error toast — attach once when the error is detected
    if (isError && xhr) {
        xhr.onreadystatechange = function () {
            if (xhr.readyState === XMLHttpRequest.DONE) {
                let message = t('exception_upload_file');
                try {
                    const parsed = JSON.parse(xhr.responseText ?? "");
                    message = parsed.message ?? message;
                } catch (_) {}
                iziToast.error({
                    title: '',
                    message,
                    position: 'bottomRight',
                    color: 'cactus--red',
                });
            }
        };
    }

    const _renderPreview = () => {
        if (isVideo(type)) {
            if (isDone) {
                return (
                    <div className="dropzone-item__img">
                        <ReactPlayer url={URL.createObjectURL(file)} controls width="100%" height="100%" />
                    </div>
                );
            }
            return (
                <div className="dropzone-item__img dropzone-item__img--placeholder">
                    <FontAwesomeIcon icon={faVideo} size="3x" />
                    <span className="dropzone-item__placeholder-name">{name}</span>
                </div>
            );
        }
        return <img className="dropzone-item__img" src={previewUrl} alt={name} title={name} />;
    }

    const _renderFooter = () => {
        if (isError) {
            return (
                <div className="dropzone-item__footer dropzone-item__footer--error">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <span className="dropzone-item__filename">{t('exception_upload_file')}</span>
                    <button className="dropzone-item__action" onClick={() => remove()}>×</button>
                </div>
            );
        }

        if (isUploading) {
            return (
                <div className="dropzone-item__footer">
                    <div className="dropzone-item__progress-track">
                        <div
                            className="dropzone-item__progress-fill"
                            style={{ width: `${Math.round(percent)}%` }}
                        />
                    </div>
                    <div className="dropzone-item__footer-row">
                        <span className="dropzone-item__filename">{name}</span>
                        <span className="dropzone-item__percent">{Math.round(percent)}%</span>
                        <button
                            className="dropzone-item__action"
                            onClick={() => { cancel(); remove(); }}
                        >
                            {t('Cancel')}
                        </button>
                    </div>
                </div>
            );
        }

        if (isDone) {
            return (
                <div className="dropzone-item__footer dropzone-item__footer--done">
                    <FontAwesomeIcon icon={faCheck} className="dropzone-item__done-icon" />
                    <span className="dropzone-item__filename">{name}</span>
                </div>
            );
        }

        return (
            <div className="dropzone-item__footer">
                <span className="dropzone-item__filename">{name}</span>
            </div>
        );
    }

    return (
        <div className={`dropzone-item${isDone ? ' dropzone-item--done' : ''}${isError ? ' dropzone-item--error' : ''}`}>
            {_renderPreview()}
            {_renderFooter()}
        </div>
    );

}

export default React.memo(DropzoneItem);
